import { useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { diffParams } from '@/model/diff';
import { useFunnelStore } from '@/store/useFunnelStore';
import type { FunnelParams } from '@/model/types';

/**
 * Emits one analytics event per actual field change.
 *
 * Rather than sprinkling `track()` through the four stage components — where it
 * would be forgotten on the next field added — this subscribes to `revision` once
 * and derives what changed with the same `diffParams` the history panel uses. One
 * mechanism, two consumers: a new field is instrumented the moment the model
 * knows about it.
 *
 * `revision` is the right trigger because the store bumps it on every params
 * mutation and deliberately does NOT bump it on `hydrate` — so loading a
 * scenario, or restoring one, never reports itself as a burst of edits.
 */
export function useParamAnalytics(enabled: boolean): void {
  const previous = useRef<FunnelParams | null>(null);

  useEffect(() => {
    if (!enabled) return;

    previous.current = useFunnelStore.getState().params;

    return useFunnelStore.subscribe((state, prior) => {
      if (state.revision === prior.revision) return;

      const before = previous.current ?? prior.params;
      previous.current = state.params;

      for (const change of diffParams(before, state.params)) {
        if (change.kind === 'added' || change.kind === 'removed') {
          track(change.kind === 'added' ? 'row_added' : 'row_removed', {
            list: change.path.split('.')[0],
            stage: change.stage,
          });
          continue;
        }
        track('param_changed', {
          path: change.path,
          stage: change.stage,
          kind: change.kind,
          from: change.from,
          to: change.to,
        });
      }
    });
  }, [enabled]);
}
